import { Injectable } from '@angular/core';
import { firstValueFrom, forkJoin, map, of, switchMap } from 'rxjs';
import { BusinessResponseDTO } from 'src/app/shared/models/dtos/register-form/business-response-dto/business-response-dto';
import { PointSummaryDTO } from 'src/app/shared/models/dtos/responses/point-summary/point-summary-dto';
import { UserService } from '../../api/user/user.service';
import { AuthenticatedUserService } from '../authenticated-user/authenticated-user.service';
import { BusinessSummaryDTO } from 'src/app/shared/models/dtos/responses/business-summary/business-summary-dto';
import { ProductService } from '../../api/product/product.service';
import { FamilyDTO } from 'src/app/shared/models/dtos/responses/family/family';
import { RewardService } from '../../api/reward/reward.service';

@Injectable({
  providedIn: 'root',
})
/**
 * Top Header Service knows what top header needs to work!
 */
export class TopHeaderService {
  pointSummary?: PointSummaryDTO;
  business?: BusinessResponseDTO;
  businessSummary?: BusinessSummaryDTO;

  /**
   * @constructor
   */
  constructor(
    private authenticatedUserService: AuthenticatedUserService,
    private userService: UserService,
    private productService: ProductService,
    private rewardService: RewardService
  ) {}

  /**
   * Fetches information necessary for top header to work
   */
  async fetchTopHeaderInfo() {
    this.businessSummary = new BusinessSummaryDTO();

    let businessId: number | undefined;
    try {
      businessId = await this.authenticatedUserService.getBusinessId();
    } catch (err) {
      return;
    }

    if (businessId == null) {
      return;
    }

    const representative = this.authenticatedUserService.representative;
    if (!representative) {
      return;
    }

    const { business } = await firstValueFrom(
      forkJoin({
        business: this.userService.getBusinessInfo(),
      })
    );

    const json = JSON.stringify(business);
    this.business = BusinessResponseDTO.deserialize(BusinessResponseDTO)(json);

    const pointsByFamily$ = this.productService.getBusinessEquipments().pipe(
      switchMap((equipments) => {
        const familiesMap = new Map<number, FamilyDTO>();

        equipments.forEach((e) => {
          if (e.family && e.family.id != null) {
            familiesMap.set(e.family.id, e.family);
          }
        });

        const uniqueFamilies = Array.from(familiesMap.values());

        if (uniqueFamilies.length === 0) {
          return of([]);
        }

        const familyPointsRequests$ = uniqueFamilies.map((family) =>
          this.rewardService.getPointSummary(family.id!).pipe(
            map((pointSummary) => ({
              family,
              pointSummary,
            }))
          )
        );

        return forkJoin(familyPointsRequests$);
      })
    );

    this.businessSummary.pointsByFamily = await firstValueFrom(pointsByFamily$);
  }
}
